import { debounce, isEqual } from 'lodash';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../../store';
import type { EjnFilter } from '../../store/reducers/ejn-filters';
import {
  fetchExtrajudicialNotifications,
  updateEjnFromList,
} from '../../store/reducers/ejn-list';
import type { ExtrajudicialNotification } from '../../types';
import type { NullableString } from '../../utils/types/nullable';

interface IEjnListContext {
  extrajudicialNotifications: ExtrajudicialNotification[];
  loading: boolean;
  error: NullableString;
  updateEjnState: (ejn: ExtrajudicialNotification) => void;
}

const defaultEjnListContext: IEjnListContext = {
  extrajudicialNotifications: [],
  loading: false,
  error: null,
  updateEjnState: () => undefined,
};

const EjnListContext = createContext(defaultEjnListContext);

export const EjnListProvider: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const dispatch = useDispatch();
  const ejnFilters = useSelector(
    (state: RootState) => state.extrajudicialNotificationsFiltersStore,
  );
  const { extrajudicialNotifications, loading, error, initialLoadComplete } =
    useSelector((state: RootState) => state.extrajudicialNotificationsStore);

  const [prevFilters, setPrevFilters] = useState(ejnFilters);

  const fetchNotifications = useCallback(
    (filters: EjnFilter, initialLoadComplete: boolean) => {
      if (!(initialLoadComplete && isEqual(filters, prevFilters))) {
        dispatch(fetchExtrajudicialNotifications(filters));
        setPrevFilters(filters);
      }
    },
    [dispatch, prevFilters],
  );

  useEffect(() => {
    fetchNotifications(ejnFilters, initialLoadComplete);
  }, [ejnFilters, fetchNotifications, initialLoadComplete]);

  const updateEjnState = useCallback(
    debounce((ejn: ExtrajudicialNotification) => {
      dispatch(updateEjnFromList(ejn));
    }, 300),
    [],
  );

  return (
    <EjnListContext.Provider
      value={{
        extrajudicialNotifications,
        error,
        loading,
        updateEjnState,
      }}
    >
      {children}
    </EjnListContext.Provider>
  );
};

export const useEjnList = () => {
  return useContext(EjnListContext);
};
